import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import * as layoutCalc from './layoutCalc';

import {
    elementAttach,
    ElementType,
    toggleDockedState,
    workspaceLayoutAddAt,
    workspaceLayoutDelete,
    workspaceLayoutResize,
    workspaceLayoutWidgetResize
} from './gridLayout';

import LayoutCell from './LayoutCell';
import { LayoutGridEdges } from './LayoutGridEdge';
import { DisplayMode } from '../../ts/stores/WorkspaceStore';

import { IEdgeFlags, ILayoutData, IWidget } from '../../ts/Interfaces';

import {
    ISizeObj,
    ISizesObj,
    IWidgetLayout,
    IWidgetLayoutElement,
    OrientationType,
    EdgeOrientationType,
    IDragState
} from './Interfaces';

import LayoutContainerBlock, { IResize } from './LayoutContainerBlock';

import { IWidgetProperties } from '../Widget';
import { showGridCustomizationDialog } from './GridCustomizationDialog';

const DEFAULT_DRAG_SCOPE = 'workspace';

export interface ILayoutGridProperties {
    viewportWidth: number;
    viewportHeight: number;
    parentLeft?: number;
    parentTop?: number;
    layout: IWidgetLayout;
    elements: IWidget[];
    displayMode: DisplayMode;
    hidden: boolean;
    showGrid: boolean;
    workspace: number;
    tab: number;
    glob: IGlob;

    dragScope?: string;
    enableResize?: boolean;
    cellAddTitle?: string;

    children?: React.ReactNode[] | React.JSX.Element;

    onLayoutChange(layout: IWidgetLayout): void;
    onElementAdd(x: number, y: number): void;
    onDrop?(payload, x: number, y: number): void;
    onSpecialAction?(widgetID: number): void;
    onTemplateDrop?(data, x: number, y: number): void;
}

interface ILayoutGridState {
    layout: IWidgetLayout;
    rowsObj?: ISizesObj;
    colsObj?: ISizesObj;

    occupyMatrix?: any[][];
    width?: number;
    height?: number;
}

function getState(layout: IWidgetLayout, height: number, width: number): ILayoutGridState {
    const layoutCopy = $.extend(true, {}, layout);
    const colsObj = layoutCalc.calculate(layoutCopy.columns, width);
    const rowsObj = layoutCalc.calculate(layoutCopy.rows, height);
    const occupyMatrix = _.map(_.range(layoutCopy.rows.length), () => {
        return [];
    });

    _.each(layoutCopy.pos, (pos, i) => {
        if (pos == null) {
            return;
        }

        const size = layoutCopy.size[i];

        if (size != null) {
            _.each(_.range(pos[0], pos[0] + size[0]), (x) => {
                _.each(_.range(pos[1], pos[1] + size[1]), (y) => {
                    if (x >= layoutCopy.rows.length || y >= layoutCopy.columns.length) {
                        return;
                    }

                    occupyMatrix[x][y] = 1;
                });
            });
        }
    });

    return {
        layout: layoutCopy,
        colsObj: colsObj,
        rowsObj: rowsObj,
        height: height,
        width: width,
        occupyMatrix: occupyMatrix
    };
}

function putStateIn<TState>(state: TState, newState: TState) {
    Object.getOwnPropertyNames(newState).forEach((name) => {
        state[name] = newState[name];
    });

    return state;
}

function useLayoutGridState(layout: IWidgetLayout, height: number, width: number) {
    const state = React.useRef<ILayoutGridState>(getState(layout, height, width));
    const [, setUpdateFlag] = React.useState(0);

    const refresh = React.useCallback(() => {
        setUpdateFlag((updateFlag) => ++updateFlag);
    }, []);

    const updateState = React.useCallback((layout: IWidgetLayout, height: number, width: number) => {
        const newState = getState(layout, height, width);

        if (JSON.stringify(newState) !== JSON.stringify(state.current)) {
            putStateIn(state.current, newState);
            refresh();
        }
    }, []);

    return { state: state.current, refresh, updateState };
}

function getEdgeFlags(gridW, gridH, x1, y1, x2, y2): IEdgeFlags {
    const top = x1 === 0;
    const left = y1 === 0;
    const right = y2 === gridW - 1;
    const bottom = x2 === gridH - 1;

    return {
        top: top,
        right: right,
        bottom: bottom,
        left: left
    };
}

function getInitialDragState(): IDragState {
    return {
        drag: false,
        dragType: null,
        dragOrient: null,
        dragIndex: null,
        dragIngoreMinFix: null,
        dragStartX: null,
        dragStartY: null,
        dragX: null,
        dragY: null
    };
}

type DragStateReducerActionType =
    | 'edgeDragStarted'
    | 'edgeMoved'
    | 'edgeDragEnded'
    | 'widgetResizeStarted'
    | 'widgetResizeMoved'
    | 'widgetResizeEnded';
type IDragStateReducer = React.Reducer<IDragState, { type: DragStateReducerActionType; newState?: IDragState }>;

const dragStateReducer: IDragStateReducer = (state: IDragState, { type, newState }) => {
    switch (type) {
        case 'edgeDragStarted':
            // At start refresh is not needed.
            state.drag = true;
            state.dragType = 'edge';
            putStateIn(state, newState);
            break;

        case 'edgeMoved':
            // Refresh to visualize moving.
            state = putStateIn({ ...state }, newState);
            break;

        case 'edgeDragEnded':
            // No refresh.
            putStateIn(state, getInitialDragState());
            break;

        case 'widgetResizeStarted':
            // At start refresh is not needed.
            state.drag = true;
            state.dragType = 'widgetResize';
            putStateIn(state, newState);
            break;

        case 'widgetResizeMoved':
            // Refresh to visualize resizing.
            state = putStateIn({ ...state }, newState);
            break;

        case 'widgetResizeEnded':
            // Refresh.
            state = getInitialDragState();
            break;
    }

    return state;
};

function getCursorPosition(e) {
    const x = e.pageX || (e.touches && e.touches.length > 0 && e.touches[0].pageX);
    const y = e.pageY || (e.touches && e.touches.length > 0 && e.touches[0].pageY);

    if (typeof x !== 'number' || typeof y !== 'number') {
        return null;
    }

    return {
        x: x,
        y: y
    };
}

export function LayoutGrid(props: ILayoutGridProperties) {
    const { state, refresh, updateState } = useLayoutGridState(props.layout, props.viewportHeight, props.viewportWidth);
    const [dragState, dispatchDragState] = React.useReducer(dragStateReducer, getInitialDragState());
    const layoutDataCollection = React.useRef<{ [widgetID: number]: ILayoutData }>({});

    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleOnMouseLeave = (e: MouseEvent) => {
            handleOnMouseUp(e);
        };

        containerRef.current.onmouseleave = handleOnMouseLeave;
    }, []);

    React.useEffect(() => {
        updateState(props.layout, props.viewportHeight, props.viewportWidth);
    }, [props.viewportHeight, props.viewportWidth, props.layout]);

    const refreshWorskpace = React.useCallback(() => {
        props.glob.AppActions.workspaceUpdateInitiate(false, props.workspace, props.tab);
    }, [props.workspace, props.tab]);

    const processOnLayoutChange = React.useCallback(
        (layout: IWidgetLayout, refresh?: boolean) => {
            if (props.onLayoutChange != null) {
                props.onLayoutChange($.extend(true, {}, layout));
            }

            if (refresh) {
                refreshWorskpace();
            }
        },
        [props.onLayoutChange, refreshWorskpace]
    );

    const handleOnEdgeDragStart = React.useCallback(
        (e, orientation: EdgeOrientationType, index: number, ignoreMinFix: boolean) => {
            const pos = getCursorPosition(e);

            if (pos == null) {
                return;
            }

            dispatchDragState({
                type: 'edgeDragStarted',
                newState: {
                    dragOrient: orientation,
                    dragIndex: index,
                    dragIngoreMinFix: ignoreMinFix,
                    dragStartX: pos.x,
                    dragStartY: pos.y
                }
            });
        },
        []
    );

    const processEdgeDragMove = (e) => {
        const pos = getCursorPosition(e);

        if (pos == null) {
            return;
        }

        const { dragOrient, dragIndex, dragIngoreMinFix, dragStartY, dragStartX } = dragState;
        const { colsObj, rowsObj } = state;

        let sizesObj: ISizesObj;

        switch (dragOrient) {
            case 'horizontal':
                sizesObj = workspaceLayoutResize(dragIndex, rowsObj, pos.y - dragStartY, dragIngoreMinFix);
                break;

            case 'vertical':
                sizesObj = workspaceLayoutResize(dragIndex, colsObj, pos.x - dragStartX, dragIngoreMinFix);
                break;
        }

        if (sizesObj == null) {
            refresh();
            return;
        }

        dispatchDragState({
            type: 'edgeMoved',
            newState: {
                dragX: pos.x,
                dragY: pos.y
            }
        });
    };

    const handleOnWidgetResizeStart = React.useCallback((e, orderIndex: number, orient: OrientationType) => {
        const pos = getCursorPosition(e);

        if (pos == null) {
            return;
        }

        dispatchDragState({
            type: 'widgetResizeStarted',
            newState: {
                dragType: 'widgetResize',
                dragOrient: orient,
                dragIndex: orderIndex,
                dragStartX: pos.x,
                dragStartY: pos.y,
                dragX: pos.x,
                dragY: pos.y
            }
        });
    }, []);

    const processWidgetResizeMove = React.useCallback((e) => {
        const pos = getCursorPosition(e);

        if (pos == null) {
            return;
        }

        dispatchDragState({
            type: 'widgetResizeMoved',
            newState: {
                dragX: pos.x,
                dragY: pos.y
            }
        });
    }, []);

    const processWidgetResizeEnd = (e) => {
        const { layout, colsObj, rowsObj } = state;
        const newLayout = workspaceLayoutWidgetResize(
            dragState.dragIndex,
            layout,
            colsObj.arr,
            rowsObj.arr,
            dragState.dragOrient as OrientationType,
            dragState.dragX - dragState.dragStartX,
            dragState.dragY - dragState.dragStartY
        );

        processOnLayoutChange(newLayout);
    };

    const processMouseMove = (e) => {
        if (!dragState.drag) {
            return;
        }

        switch (dragState.dragType) {
            case 'edge':
                processEdgeDragMove(e);
                break;

            case 'widgetResize':
                processWidgetResizeMove(e);
                break;
        }

        e.stopPropagation();
        e.preventDefault();
    };

    const handleOnMouseUp = (e) => {
        if (!dragState.drag) {
            return;
        }

        if (dragState.dragType === 'widgetResize') {
            processWidgetResizeEnd(e);
            dispatchDragState({
                type: 'widgetResizeEnded'
            });
        } else {
            dispatchDragState({
                type: 'edgeDragEnded'
            });
            processOnLayoutChange(state.layout);
        }
    };

    const getOnToggleDockedStateHandler = (colSizeObj: ISizeObj, rowSizeObj: ISizeObj) => {
        let toggleColumn: boolean;

        if (colSizeObj != null && colSizeObj.origin.allowDocking) {
            toggleColumn = true;
        } else if (rowSizeObj != null && rowSizeObj.origin.allowDocking) {
            toggleColumn = false;
        } else {
            return;
        }

        return () => {
            if (toggleColumn) {
                toggleDockedState(colSizeObj);
            } else {
                toggleDockedState(rowSizeObj);
            }

            processOnLayoutChange(state.layout, true);

            if (rowSizeObj != null && !rowSizeObj.origin.docked) {
                props.glob.AppActions.widgetRepaintOnDock();
            }
        };
    };

    const handleOnSpecialAction = React.useCallback(
        (widgetID: number) => {
            return (e) => {
                if (props.onSpecialAction != null) {
                    props.onSpecialAction(widgetID);
                }
            };
        },
        [props.onSpecialAction]
    );

    const { colsObj, rowsObj, layout, height, width, occupyMatrix } = state;

    const getLayoutRows = () => {
        if (props.displayMode === 'main') {
            return null;
        }

        const handleOnCellDrop = (payload, x: number, y: number) => {
            if (payload.type === 'widget') {
                const newLayout = elementAttach(state.layout, payload.orderIndex, x, y, payload.remainSize, null);

                if (newLayout == null) {
                    return;
                }

                processOnLayoutChange(newLayout);
            }

            if (typeof props.onDrop === 'function') {
                props.onDrop(payload, x, y);
            }
        };

        return (
            <LayoutRows
                colsArr={colsObj.arr}
                rowsArr={rowsObj.arr}
                occupyMatrix={occupyMatrix}
                dragScope={props.dragScope}
                cellAddTitle={props.cellAddTitle}
                onCellDrop={handleOnCellDrop}
                onAdd={props.onElementAdd}
            />
        );
    };

    const columnSizes = colsObj.arr;
    const rowSizes = rowsObj.arr;
    const gridWidth = layout.columns.length;
    const gridHeight = layout.rows.length;
    const { drag, dragType, dragIndex, dragOrient, dragX, dragY, dragStartX, dragStartY } = dragState;

    const widgets = _.map(props.elements, (widget, index) => {
        if (widget == null) {
            return;
        }

        const pos = layout.pos[index];
        const size = layout.size[index];

        if (pos == null || size == null) {
            return;
        }

        const style = layoutCalc.render(pos, size, columnSizes, rowSizes);

        if (style == null) {
            return;
        }

        const edgeFlags = getEdgeFlags(
            gridWidth,
            gridHeight,
            pos[0],
            pos[1],
            pos[0] + size[0] - 1,
            pos[1] + size[1] - 1
        );

        if (Number.isNaN(style.height)) {
            delete style.height;
        }

        if (Number.isNaN(style.width)) {
            delete style.width;
        }

        if (Number.isNaN(style.right)) {
            delete style.right;
        }

        if (Number.isNaN(style.bottom)) {
            delete style.bottom;
        }

        if (Number.isNaN(style.left)) {
            delete style.left;
        }

        const cell = {
            width: 1,
            height: 1
        };

        const columnPosition = pos[1];
        const colSizeObj = columnSizes[columnPosition];
        const rowPosition = pos[0];
        const rowSizeObj = rowSizes[rowPosition];

        if (colSizeObj != null && rowSizeObj != null) {
            if (!Number.isNaN(colSizeObj.size)) {
                cell.width = colSizeObj.size;
            }

            if (!Number.isNaN(rowSizeObj.size)) {
                cell.height = rowSizeObj.size;
            }
        }

        let resizeObj: IResize;

        if (drag && dragType === 'widgetResize' && dragIndex === index) {
            resizeObj = {
                orient: dragOrient === 'horizontal' ? 'right' : dragOrient === 'vertical' ? 'bottom' : dragOrient,
                dx: dragX - dragStartX,
                dy: dragY - dragStartY
            };
        }

        const widgetID = widget.id || widget.cid;

        let child = props.children[index];

        if (child != null) {
            const dockedVertically = colSizeObj?.origin?.docked === true;
            const docked = dockedVertically || rowSizeObj?.origin?.docked === true;

            let widgetProps: Partial<IWidgetProperties>;
            let layoutData = layoutDataCollection.current[widgetID];

            if (layoutData == null) {
                layoutData = layoutDataCollection.current[widgetID] = {
                    docked: docked,
                    dockedVertically: dockedVertically,
                    parentLeft: style.left as number,
                    parentTop: style.top as number,
                    parentWidth: style.width as number,
                    parentHeight: style.height as number
                };
            } else {
                layoutData.docked = docked;
                layoutData.dockedVertically = dockedVertically;
                layoutData.parentLeft = style.left as number;
                layoutData.parentTop = style.top as number;
                layoutData.parentWidth = style.width as number;
                layoutData.parentHeight = style.height as number;
            }

            if (child.type !== 'div') {
                widgetProps = {
                    edgeFlags: edgeFlags,
                    layoutData: layoutData,
                    onToggleDocking: getOnToggleDockedStateHandler(colSizeObj, rowSizeObj)
                };
            }

            child = React.cloneElement(child, widgetProps);
        }

        return (
            <LayoutContainerBlock
                orderIndex={index}
                key={index}
                style={style}
                cell={cell}
                dragScope={props.dragScope || DEFAULT_DRAG_SCOPE}
                dragAllowed={props.displayMode === 'widgets'}
                hidden={props.hidden}
                enableResize={props.enableResize}
                edgeFlags={edgeFlags}
                resize={resizeObj}
                onResizeStart={handleOnWidgetResizeStart}
                onSpecialAction={handleOnSpecialAction(widgetID)}
            >
                {child}
            </LayoutContainerBlock>
        );
    });

    const style: React.CSSProperties = {};
    const colsContainerSize = colsObj.containerSize;
    const rowsObjContainerSize = rowsObj.containerSize;

    if (colsContainerSize != null && width !== colsContainerSize) {
        style.width = colsContainerSize;
    }

    if (rowsObjContainerSize != null && height !== rowsObjContainerSize) {
        style.height = rowsObjContainerSize;
    }

    return (
        <div
            ref={containerRef}
            className={
                'layout-container' +
                (drag && dragType === 'widgetResize' ? ' widget-resize' : '') +
                ' mode-' +
                props.displayMode +
                (props.showGrid ? ' mode-grid' : '')
            }
            onMouseMove={processMouseMove}
            onMouseUp={handleOnMouseUp}
            onTouchMove={processMouseMove}
            onTouchEnd={handleOnMouseUp}
            onTouchCancel={handleOnMouseUp}
        >
            <div className="inner-layout-container" style={style}>
                {getLayoutRows()}
                <LayoutGridEdges
                    orientation={'vertical'}
                    sizes={columnSizes}
                    dragState={dragState}
                    parentLeft={props.parentLeft}
                    showGrid={props.showGrid}
                    onDragStart={handleOnEdgeDragStart}
                />
                <LayoutGridEdges
                    orientation={'horizontal'}
                    sizes={rowSizes}
                    dragState={dragState}
                    parentLeft={props.parentLeft}
                    showGrid={props.showGrid}
                    onDragStart={handleOnEdgeDragStart}
                />
                {props.showGrid ? (
                    <LayoutControls state={state} glob={props.glob} onLayoutChange={processOnLayoutChange} />
                ) : undefined}
                {widgets}
            </div>
        </div>
    );
}

function LayoutControls(props: {
    state: ILayoutGridState;
    glob: IGlob;
    onLayoutChange(layout: IWidgetLayout, refresh?: boolean): void;
}) {
    const { state } = props;
    const { colsObj, rowsObj, layout } = state;
    const columnSizes = colsObj.arr;
    const rowSizes = rowsObj.arr;

    const handleOnAddAfterClick = (elementType: ElementType, index: number, layoutArr: IWidgetLayoutElement[]) => {
        workspaceLayoutAddAt(state.layout, elementType, index + 1, layoutArr);
        props.onLayoutChange(state.layout);
    };

    const handleOnDeleteButtonClick = (elementType: ElementType, index: number, layoutArr: IWidgetLayoutElement[]) => {
        workspaceLayoutDelete(state.layout, elementType, index, layoutArr, props.glob);
        props.onLayoutChange(state.layout);
    };

    const handleOnConfigButtonClick = (
        obj: ISizeObj,
        sizesObj: ISizesObj,
        elementType: ElementType,
        isLast: boolean
    ) => {
        showGridCustomizationDialog(obj, sizesObj, elementType, isLast, props.glob, (refresh: boolean) => {
            props.onLayoutChange(state.layout, refresh);
        });
    };

    const result: React.JSX.Element[] = [];
    const lastColumnIndex = columnSizes.length - 1;

    let shift = 0;

    for (let index = 0; index <= lastColumnIndex; index++) {
        const obj = columnSizes[index];
        const style: React.CSSProperties = {
            left: shift
        };

        if (obj.size != null && !Number.isNaN(obj.size)) {
            style.width = obj.size;
            shift += obj.size;
        }

        result.push(
            <LayoutControl
                key={'column-layout-control-' + index}
                layout={layout}
                sizeObj={obj}
                sizesObj={colsObj}
                orientation={'vertical'}
                style={style}
                index={index}
                lastIndex={lastColumnIndex}
                onAddAfter={handleOnAddAfterClick}
                onDelete={handleOnDeleteButtonClick}
                onConfigButtonClick={handleOnConfigButtonClick}
            />
        );
    }

    const lastRowIndex = rowSizes.length - 1;

    shift = 0;

    for (let index = 0; index <= lastRowIndex; index++) {
        const obj = rowSizes[index];
        const style: React.CSSProperties = {
            top: shift
        };

        if (obj.size != null && !Number.isNaN(obj.size)) {
            style.height = obj.size;
            shift += obj.size;
        }

        result.push(
            <LayoutControl
                key={'row-layout-control_' + index}
                layout={layout}
                sizeObj={obj}
                sizesObj={rowsObj}
                orientation={'horizontal'}
                style={style}
                index={index}
                lastIndex={lastRowIndex}
                onAddAfter={handleOnAddAfterClick}
                onDelete={handleOnDeleteButtonClick}
                onConfigButtonClick={handleOnConfigButtonClick}
            />
        );
    }

    return result;
}

function isLayoutControlSmall(sizeObj: ISizeObj, orientation: EdgeOrientationType) {
    let threshold = 0;

    switch (orientation) {
        case 'vertical':
            threshold = 105;
            break;

        case 'horizontal':
            threshold = 118;
    }

    return sizeObj.size < threshold;
}

function LayoutControl(props: {
    layout: IWidgetLayout;
    sizeObj: ISizeObj;
    sizesObj: ISizesObj;
    orientation: EdgeOrientationType;
    style: React.CSSProperties;
    index: number;
    lastIndex: number;
    onAddAfter(elementType: ElementType, index: number, layoutArr: IWidgetLayoutElement[]): void;
    onDelete(elementType: ElementType, index: number, layoutArr: IWidgetLayoutElement[]): void;
    onConfigButtonClick(obj: ISizeObj, sizesObj: ISizesObj, elementType: ElementType, isLast: boolean): void;
}) {
    const { layout, orientation, index, lastIndex } = props;

    let addBefore: React.JSX.Element;
    let addAfter: React.JSX.Element;
    let elementType: ElementType;

    if (orientation === 'vertical') {
        elementType = 'column';

        addAfter = (
            <div
                className="layout-button"
                onClick={() => {
                    props.onAddAfter(elementType, index, layout.columns);
                }}
            >
                <span className="glyphicon glyphicon glyphicon glyphicon-plus"></span>
            </div>
        );

        if (index === 0) {
            addBefore = (
                <div
                    className="layout-button"
                    onClick={() => {
                        props.onAddAfter(elementType, index - 1, layout.columns);
                    }}
                >
                    <span className="glyphicon glyphicon glyphicon glyphicon-plus"></span>
                </div>
            );
        }
    }

    if (orientation === 'horizontal') {
        elementType = 'row';

        addAfter = (
            <div
                className="layout-button"
                onClick={() => {
                    props.onAddAfter(elementType, index, layout.rows);
                }}
            >
                <span className="glyphicon glyphicon glyphicon glyphicon-plus"></span>
            </div>
        );

        if (index === 0) {
            addBefore = (
                <div
                    className="layout-button"
                    onClick={() => {
                        props.onAddAfter(elementType, index - 1, layout.rows);
                    }}
                >
                    <span className="glyphicon glyphicon glyphicon glyphicon-plus"></span>
                    <br />
                </div>
            );
        }
    }

    let deleteButton: React.JSX.Element;

    if (lastIndex > 0) {
        let elementType: ElementType;
        let layoutArr: IWidgetLayoutElement[];

        if (orientation === 'horizontal') {
            elementType = 'row';
            layoutArr = layout.rows;
        } else {
            elementType = 'column';
            layoutArr = layout.columns;
        }

        deleteButton = (
            <div
                className="layout-button"
                onClick={() => {
                    props.onDelete(elementType, index, layoutArr);
                }}
            >
                <span className="glyphicon glyphicon-trash"></span>
            </div>
        );
    }

    let className = 'layout-control ' + orientation;

    if (isLayoutControlSmall(props.sizeObj, orientation)) {
        className += ' small';
    }

    if (index === 0) {
        className += ' first';
    } else if (index === lastIndex) {
        className += ' last';
    }

    return (
        <div key={'layout-control-' + orientation + '-' + index} className={className} style={props.style}>
            <div className="layout-control-inner">
                <div className="small-container">
                    <div className="layout-button button-small">
                        <span className="glyphicon glyphicon-pencil"></span>
                    </div>
                </div>

                {addBefore}

                <div
                    className={'layout-button'}
                    onClick={() => {
                        props.onConfigButtonClick(props.sizeObj, props.sizesObj, elementType, index === lastIndex);
                    }}
                >
                    <span className={'glyphicon glyphicon-cog'} />
                </div>

                {deleteButton}

                {addAfter}
            </div>
        </div>
    );
}

function LayoutRow(props: {
    occupyMatrix: any[][];
    obj: ISizeObj;
    colsArr: ISizeObj[];
    shiftX: number;
    x: number;
    dragScope: string;
    cellAddTitle: string;
    onCellDrop(payload, x: number, y: number): void;
    onAdd(x: number, y: number): void;
}) {
    const { occupyMatrix, x } = props;

    let shiftY = 0;

    const cells = _.map(props.colsArr, (colObj, y) => {
        const cell = (
            <LayoutCell
                key={'layout-cell' + x + '-' + y}
                x={x}
                y={y}
                colObj={colObj}
                shiftY={shiftY}
                occupied={occupyMatrix[x][y] === 1}
                dragScope={props.dragScope || DEFAULT_DRAG_SCOPE}
                addTitle={props.cellAddTitle}
                onDrop={props.onCellDrop}
                onAdd={props.onAdd}
            />
        );

        shiftY += colObj.size;
        return cell;
    });

    return (
        <div
            key={'layout-row' + x}
            className="layout-row"
            style={{
                height: props.obj.size,
                top: props.shiftX
            }}
        >
            {cells}
        </div>
    );
}

function LayoutRows(props: {
    colsArr: ISizeObj[];
    rowsArr: ISizeObj[];
    occupyMatrix: any[][];
    dragScope: string;
    cellAddTitle: string;
    onCellDrop(payload, x: number, y: number): void;
    onAdd(x: number, y: number): void;
}) {
    let shift = 0;

    return _.map(props.rowsArr, (obj, x) => {
        const row = (
            <LayoutRow
                key={'layout-row_' + x}
                occupyMatrix={props.occupyMatrix}
                obj={obj}
                colsArr={props.colsArr}
                shiftX={shift}
                x={x}
                dragScope={props.dragScope}
                cellAddTitle={props.cellAddTitle}
                onCellDrop={props.onCellDrop}
                onAdd={props.onAdd}
            />
        );

        shift += obj.size;

        return row;
    });
}
